/* ==========================================================================
   Stealth Pipes V2 behaviors (Koalaty Commerce, Sept 2026)
   - Muffler option cards: image, sound meter, live price delta, "most popular"
   - Sound profile badge that follows the selected muffler on full systems
   - Lead time line that adds Polished / Cerakote weeks when the finish changes
   - Sticky add-to-cart bar (appears once the real button scrolls away)
   - Bike finder + persistent "Your bike" chip (localStorage, no app)
   - Cart drawer add-ons (one tap, rebuilds Impulse's drawer)
   Everything is additive and nil-safe: missing markup means the feature stays off.
   ========================================================================== */
(function () {
  'use strict';


  function money(cents) {
    var fmt = (window.theme && theme.settings && theme.settings.moneyFormat) || '${{amount}}';
    var val = (Math.abs(cents) / 100).toFixed(2);
    var parts = val.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var noDec = fmt.indexOf('no_decimals') > -1;
    var out = noDec ? parts[0] : parts.join('.');
    return fmt.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, out).replace(/<[^>]+>/g, '');
  }

  function profileOf(text) {
    var t = (text || '').toLowerCase();
    if (t.indexOf('balanced') > -1) return 'Balanced';
    if (t.indexOf('aggressive') > -1) return 'Aggressive';
    if (t.indexOf('extreme') > -1) return 'Extreme';
    return '';
  }

  function meterSVG(profile, size) {
    var steps = { Balanced: 1, Aggressive: 2, Extreme: 3 }[profile] || 0;
    if (!steps) return '';
    var bar = function (x, y, h, lit) {
      return '<rect x="' + x + '" y="' + y + '" width="8" height="' + h + '" rx="1.5" class="sp-meter__bar' + (lit ? ' is-lit' : '') + '"/>';
    };
    return '<span class="sp-meter sp-meter--' + (size || 'xs') + ' sp-meter--' + profile.toLowerCase() + '" data-sound-meter data-level="' + profile + '" role="img" aria-label="' + profile + ' sound profile">' +
      '<svg class="sp-meter__bars" viewBox="0 0 30 22" aria-hidden="true" focusable="false">' +
      bar(0, 12, 10, steps >= 1) + bar(11, 6, 16, steps >= 2) + bar(22, 0, 22, steps >= 3) +
      '</svg><span class="sp-meter__label">' + profile + '</span></span>';
  }

  /* Map a muffler option value to a muffler product handle, so the card can show its photo. */
  var MUFFLER_IMAGES = window.SP_MUFFLER_IMAGES || {};
  function mufflerImage(value) {
    var v = value.toLowerCase();
    var keys = Object.keys(MUFFLER_IMAGES);
    for (var i = 0; i < keys.length; i++) {
      if (v.indexOf(keys[i]) > -1) return MUFFLER_IMAGES[keys[i]];
    }
    return '';
  }

  /* ---------------------------------------------------------------------- */
  /* Product section                                                         */
  /* ---------------------------------------------------------------------- */
  function initProduct(section) {
    if (!section || section.dataset.spInit) return;
    /* PDP V3 (round 2 item 10) runs its own buy box script (assets/sp-pdp.js): deltas, build dates, sticky bar. */
    if (section.hasAttribute('data-sp-pdp3')) return;
    section.dataset.spInit = '1';

    var variantsEl = section.querySelector('[data-variant-json]');
    var variants = [];
    try { variants = JSON.parse(variantsEl.textContent); } catch (e) { variants = []; }
    if (!variants.length) return;

    var optionWraps = Array.prototype.slice.call(section.querySelectorAll('.variant-input-wrap'));
    var isModal = section.dataset.modal === 'true';
    var mufflerWrap = optionWraps.filter(function (w) {
      var h = (w.dataset.handle || '');
      if (w.dataset.spRole === 'finish') return false; /* finishes filed under a "Muffler" option (Nightster HP) */
      return h === 'muffler' || h === 'muffler-type' || h === 'mufflers';
    })[0];
    var popular = (section.dataset.spPopular || '').toLowerCase();
    /* Dealer-aware price (Wholesale Gorilla rules, snippets/sp-dealer-price.liquid). Retail: v.price unchanged.
       null = a dealer rule we cannot mirror, so that price is hidden instead of shown wrong. */
    var productId = section.dataset.productId;
    function priceOf(v) { return window.SP && SP.dealerPrice ? SP.dealerPrice(v.price, productId, v.id) : v.price; }

    function selectedOptions() {
      var out = {};
      section.querySelectorAll('[data-variant-input]').forEach(function (el) {
        var idx = el.dataset.index;
        if (!idx) return;
        if (el.tagName === 'SELECT') out[idx] = el.value;
        else if (el.checked) out[idx] = el.value;
      });
      return out;
    }
    function currentVariant() {
      var sel = selectedOptions();
      return variants.filter(function (v) {
        return Object.keys(sel).every(function (k) { return v[k] === sel[k]; });
      })[0] || null;
    }
    function priceFor(optionIndex, value) {
      var sel = selectedOptions();
      var matches = variants.filter(function (v) {
        if (v[optionIndex] !== value) return false;
        return Object.keys(sel).every(function (k) { return k === optionIndex || v[k] === sel[k]; });
      });
      if (!matches.length) matches = variants.filter(function (v) { return v[optionIndex] === value; });
      if (!matches.length) return null;
      var prices = matches.map(priceOf);
      if (prices.some(function (x) { return x === null; })) return null;
      return Math.min.apply(null, prices);
    }

    /* Live price deltas for any option rendered as cards ([data-sp-delta] per value).
       Relative to the current selection, same rule everywhere on the page:
       Selected / Same price / +$X / -$X. */
    function makeDeltaUpdater(wrap) {
      var optionIndex = wrap.dataset.index;
      return function () {
        var cur = currentVariant();
        if (!cur) return;
        wrap.querySelectorAll('[data-sp-delta]').forEach(function (el) {
          var p = priceFor(optionIndex, el.dataset.value);
          if (p === null) { el.textContent = ''; return; }
          var curPrice = priceOf(cur);
          if (curPrice === null) { el.textContent = ''; return; }
          var d = p - curPrice;
          var selected = cur[optionIndex] === el.dataset.value;
          el.classList.toggle('is-included', selected || d === 0);
          if (selected) el.textContent = 'Selected';
          else if (d === 0) el.textContent = 'Same price';
          else el.textContent = (d > 0 ? '+' : '-') + money(d);
        });
      };
    }
    function badgeDisplayName(value) {
      var v = (value || '').toLowerCase();
      if (v.indexOf('custom') > -1) return 'Custom Badge';
      if (v === 'yes' || v.indexOf('stealth badge') > -1) return 'Stealth Badge';
      return 'No Badge';
    }

    /* Badge cards (Stealth Badge / No Badge / Custom Badge): Liquid renders them, JS fills deltas */
    var badgeWrap = optionWraps.filter(function (w) { return (w.dataset.handle || '').indexOf('badge') > -1; })[0];
    if (badgeWrap) {
      var updateBadgeDeltas = makeDeltaUpdater(badgeWrap);
      updateBadgeDeltas();
      section.addEventListener('variantChange', updateBadgeDeltas);
      document.addEventListener('sp:dealer-ready', updateBadgeDeltas);
      section.addEventListener('change', function (e) { if (e.target.matches('[data-variant-input]')) setTimeout(updateBadgeDeltas, 0); });
    }

    /* Muffler cards */
    if (mufflerWrap) {
      var optionIndex = mufflerWrap.dataset.index;
      mufflerWrap.querySelectorAll('.variant-input').forEach(function (wrap) {
        var input = wrap.querySelector('input[type="radio"]');
        var label = wrap.querySelector('label');
        if (!input || !label || label.dataset.spCard) return; /* Liquid renders the cards; JS only fills deltas */
        label.dataset.spCard = '1';
        var raw = input.value;
        var profile = profileOf(raw);
        var clean = raw.replace(/\s*\((balanced|aggressive|extreme)[^)]*\)\s*/i, '').replace(/\s*muffler\s*$/i, '').trim();
        var img = mufflerImage(raw);
        var isPop = popular && raw.toLowerCase().indexOf(popular) > -1;
        label.innerHTML =
          (img ? '<img class="sp-mp__img" src="' + img + '" alt="" loading="lazy" width="56" height="56">' : '<span class="sp-mp__img" aria-hidden="true"></span>') +
          '<span class="sp-mp__name">' + clean + '</span>' +
          '<span class="sp-mp__meter">' + meterSVG(profile, 'xs') + '</span>' +
          (isPop ? '<span class="sp-mp__pop">Most popular</span>' : '') +
          '<span class="sp-mp__delta" data-sp-delta data-value="' + raw.replace(/"/g, '&quot;') + '"></span>';
      });

      var updateDeltas = makeDeltaUpdater(mufflerWrap);
      updateDeltas();
      /* Deep link from the sound profile cards: ?sp_muffler=<regex> preselects that muffler and lands on the picker */
      section.addEventListener('variantChange', updateDeltas);
      document.addEventListener('sp:dealer-ready', updateDeltas);
      setTimeout(function () {
        try {
          var want = (new URLSearchParams(window.location.search).get('sp_muffler') || '').trim().toLowerCase().slice(0, 80);
          if (!want || isModal) return;
          /* text match, never a RegExp built from the URL (a crafted pattern can hang the tab); ^ = starts with */
          var start = want.charAt(0) === '^';
          if (start) want = want.slice(1);
          if (!want) return;
          var radios = Array.prototype.slice.call(mufflerWrap.querySelectorAll('input[type="radio"]'));
          var hit = radios.filter(function (i) { var s = i.value.toLowerCase(); return start ? s.indexOf(want) === 0 : s.indexOf(want) > -1; })[0];
          if (!hit) return;
          if (!hit.checked) { hit.checked = true; hit.dispatchEvent(new Event('change', { bubbles: true })); }
          setTimeout(updateDeltas, 50);
          setTimeout(function () { mufflerWrap.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' }); }, 500);
        } catch (e) {}
      }, 0);
      section.addEventListener('change', function (e) { if (e.target.matches('[data-variant-input]')) setTimeout(updateDeltas, 0); });

      /* Phones: with 5+ mufflers, show the most bought muffler of each sound profile plus the selected one, and a
         "Show all" button. The other cards stay in the DOM (deltas, badge sync and the kit logic are unchanged); the
         CSS only hides them under 481px. Ranking is from the order file (system units, all time, Aug 2026 export).
         Runs after the ?sp_muffler= deep link above, and opens the full list if a hidden muffler gets selected. */
      setTimeout(function () {
        var items = Array.prototype.slice.call(mufflerWrap.querySelectorAll('.variant-input'));
        if (items.length < 5 || mufflerWrap.dataset.spMore) return;
        mufflerWrap.dataset.spMore = '1';
        /* Liquid picks the cards (variant-button.liquid) so the list never shrinks after load; this builds the same
           set only when the server did not (older markup, quick view). */
        var btn = mufflerWrap.querySelector('.sp-mp-more');
        var hidden;
        if (btn) {
          hidden = items.filter(function (it) { return it.classList.contains('sp-mp-hide'); });
        } else {
          var RANK = [/reverse cone/i, /^(?!.*mid-length).*pipe bomb/i, /turnout/i, /turndown|streetsweeper/i, /mid-length/i, /breacher/i, /burner/i];
          var rank = function (v) { for (var i = 0; i < RANK.length; i++) { if (RANK[i].test(v)) return i; } return 99; };
          var valueOf = function (it) { var i = it.querySelector('input'); return i ? i.value : ''; };
          var best = {};
          items.forEach(function (it) {
            var p = profileOf(valueOf(it)) || 'none';
            if (!best[p] || rank(valueOf(it)) < rank(valueOf(best[p]))) best[p] = it;
          });
          var keep = Object.keys(best).map(function (k) { return best[k]; });
          hidden = items.filter(function (it) { var i = it.querySelector('input'); return keep.indexOf(it) < 0 && !(i && i.checked); });
          if (!hidden.length) return;
          hidden.forEach(function (it) { it.classList.add('sp-mp-hide'); });
          btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'btn btn--secondary btn--small sp-mp-more';
          btn.textContent = 'Show all ' + items.length + ' mufflers';
          mufflerWrap.appendChild(btn);
        }
        var showAll = function () { hidden.forEach(function (it) { it.classList.remove('sp-mp-hide'); }); btn.remove(); };
        btn.addEventListener('click', showAll);
        var openIfHidden = function () {
          var c = mufflerWrap.querySelector('input:checked');
          if (c && c.closest('.sp-mp-hide')) showAll();
        };
        mufflerWrap.addEventListener('change', openIfHidden);
        openIfHidden(); /* a ?sp_muffler= deep link may have picked a card the server hid */
      }, 0);
    }

    /* Header badge follows the selected muffler on full systems */
    var badge = section.querySelector('[data-sound-profile-dynamic]');
    if (badge && mufflerWrap) {
      var syncBadge = function () {
        var checked = mufflerWrap.querySelector('input[type="radio"]:checked');
        var p = checked ? profileOf(checked.value) : '';
        badge.innerHTML = p ? '<span class="sp-profile__eyebrow">Sound profile</span>' + meterSVG(p, 'md') : '';
      };
      syncBadge();
      section.addEventListener('variantChange', syncBadge);
      section.addEventListener('change', function (e) { if (e.target.matches('[data-variant-input]')) setTimeout(syncBadge, 0); });
    }

    /* Selected value next to each option label */
    optionWraps.forEach(function (wrap) {
      var id = wrap.id;
      var lab = id ? section.querySelector('label[for="' + id + '"]') : null;
      if (!lab) return;
      var out = document.createElement('span');
      out.className = 'variant__label-value';
      lab.appendChild(out);
      var isBadge = (wrap.dataset.handle || '').indexOf('badge') > -1;
      var sync = function () {
        var checked = wrap.querySelector('input[type="radio"]:checked');
        if (!checked) { out.textContent = ''; return; }
        out.textContent = isBadge ? badgeDisplayName(checked.value) : checked.value.replace(/\s*\((balanced|aggressive|extreme)[^)]*\)\s*/i, '');
      };
      sync();
      wrap.addEventListener('change', sync);
    });

    /* Lead time by finish */
    var lead = section.querySelector('[data-lead-time]');
    if (lead && lead.dataset.override !== 'true') {
      var min = parseInt(lead.dataset.min, 10), max = parseInt(lead.dataset.max, 10);
      var polAdd = parseInt(lead.dataset.polished, 10) || 0, cerAdd = parseInt(lead.dataset.cerakote, 10) || 0;
      var now = Date.now(); /* D15: the browser's today, never a cached server time */
      var line = lead.querySelector('[data-lead-line]');
      var fmt = function (ms) { return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
      var touched = false;
      var syncLead = function () {
        var sel = selectedOptions();
        var finish = Object.keys(sel).map(function (k) { return sel[k]; }).join(' ').toLowerCase();
        var add = 0, why = '';
        if (finish.indexOf('polish') > -1) { add = polAdd; why = ' for Polished'; }
        else if (finish.indexOf('cerakote') > -1 || finish.indexOf('black') > -1) { add = cerAdd; why = ' for Cerakote'; }
        if (add === 0 && !touched) return;
        touched = true;
        var a = min + add, b = max + add;
        var wk = 7 * 24 * 3600 * 1000;
        /* the sentence comes from Theme settings (stealth_lead_line_system via data-tpl), never from this file */
        var tpl = lead.dataset.tpl || '';
        line.textContent = tpl.replace('[min]', a).replace('[max]', b) + why;
        line.insertAdjacentHTML('beforeend', '<span data-lead-dates> (' + fmt(now + a * wk) + ' to ' + fmt(now + b * wk) + ')</span>.');
      };
      syncLead();
      section.addEventListener('variantChange', syncLead);
      section.addEventListener('change', function (e) { if (e.target.matches('[data-variant-input]')) setTimeout(syncLead, 0); });
    }

    /* Sticky add to cart */
    var atc = section.querySelector('[data-add-to-cart]');
    if (atc && !isModal && !document.querySelector('.sp-sticky')) {
      var titleEl = section.querySelector('.product-single__title');
      var imgEl = section.querySelector('.product__main-photos img');
      var bar = document.createElement('div');
      bar.className = 'sp-sticky';
      bar.setAttribute('role', 'region'); /* axe region: the bar sits outside main */
      bar.setAttribute('aria-label', 'Add to cart bar');
      bar.setAttribute('aria-hidden', 'true');
      bar.inert = true;
      bar.innerHTML =
        '<div class="sp-sticky__inner">' +
          (imgEl ? '<img class="sp-sticky__img" src="' + (imgEl.currentSrc || imgEl.src) + '" alt="">' : '') +
          '<div class="sp-sticky__meta"><span class="sp-sticky__title">' + (titleEl ? titleEl.textContent.trim() : '') + '</span><span class="sp-sticky__sub" data-sp-sticky-sub></span></div>' +
          '<span class="sp-sticky__price" data-sp-sticky-price></span>' +
          '<button type="button" class="btn sp-sticky__btn" data-sp-sticky-btn>Add to cart</button>' +
        '</div>';
      /* Stars next to the name, desktop only (CSS). Same element the title shows, so the numbers always agree. */
      var ratingEl = section.querySelector('.sp-rating--md');
      if (ratingEl) {
        var rc = document.createElement('span');
        rc.className = 'sp-rating sp-rating--xs sp-sticky__rating';
        rc.setAttribute('aria-hidden', 'true');
        rc.innerHTML = ratingEl.innerHTML;
        bar.querySelector('.sp-sticky__meta').appendChild(rc);
      }
      document.body.appendChild(bar);
      document.body.classList.add('sp-has-sticky');
      /* Shopify's theme-preview bar sits at the bottom of the viewport; stack above it. */
      var previewBar = document.getElementById('preview-bar-iframe');
      if (previewBar) bar.style.bottom = (previewBar.offsetHeight || 60) + 'px';
      var sPrice = bar.querySelector('[data-sp-sticky-price]');
      var sSub = bar.querySelector('[data-sp-sticky-sub]');
      var sBtn = bar.querySelector('[data-sp-sticky-btn]');
      var syncSticky = function () {
        var cur = currentVariant();
        if (cur) {
          var sp = priceOf(cur);
          sPrice.textContent = sp === null ? '' : money(sp);
          var sel = selectedOptions();
          sSub.textContent = Object.keys(sel).map(function (k) { return sel[k].replace(/\s*\((balanced|aggressive|extreme)[^)]*\)\s*/i, ''); }).join(' / ');
          sBtn.disabled = !cur.available;
          sBtn.textContent = cur.available ? 'Add to cart' : 'Sold out';
        }
      };
      syncSticky();
      section.addEventListener('variantChange', syncSticky);
      document.addEventListener('sp:dealer-ready', syncSticky);
      section.addEventListener('change', function (e) { if (e.target.matches('[data-variant-input]')) setTimeout(syncSticky, 0); });
      sBtn.addEventListener('click', function () {
        /* Route through the real button so the Custom Badge submit gate and Impulse's ajax add both run. */
        atc.click();
      });
      /* Visible whenever the inline Add to cart is out of view, above or below (round 2 parity A, Big Gun ruling), so
         a phone landing has a buy action on screen 1. Never over the footer, never while a drawer is open. While
         hidden it is inert and aria-hidden, so nothing in it takes focus (axe aria-hidden-focus). */
      var atcOut = false, footerIn = false;
      var footerEl = document.querySelector('.site-footer');
      var applySticky = function () {
        /* any overlay hides the bar: Impulse drawers (html.js-drawer-open), the round 2 slide-out cart
           (body.sp-cd-open), the nav drawer (html.sp-nav-open) and the My Bike sheet (body.sp-garage-open) */
        var h = document.documentElement.classList, bd = document.body.classList;
        var overlay = h.contains('js-drawer-open') || h.contains('sp-nav-open') || bd.contains('sp-cd-open') || bd.contains('sp-garage-open');
        var show = atcOut && !footerIn && !overlay;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        bar.inert = !show;
      };
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { atcOut = !en.isIntersecting; });
          applySticky();
        }, { threshold: 0 }).observe(atc);
        if (footerEl) new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { footerIn = en.isIntersecting; });
          applySticky();
        }, { threshold: 0 }).observe(footerEl);
      }
      new MutationObserver(applySticky).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      new MutationObserver(applySticky).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

  }

  /* ---------------------------------------------------------------------- */
  /* Cart add-ons                                                            */
  /* ---------------------------------------------------------------------- */
  function initAddons() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-sp-addon-add]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      var id = btn.dataset.spAddonAdd;
      btn.disabled = true;
      var original = btn.textContent;
      btn.textContent = 'Adding';
      fetch(theme.routes.cartAdd, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: id, quantity: 1 })
      }).then(function (r) { return r.json(); }).then(function (data) {
        if (data && data.status && data.status !== 200) throw new Error(data.description || 'error');
        btn.textContent = 'Added';
        btn.classList.add('is-added');
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:open'));
      }).catch(function () {
        btn.disabled = false;
        btn.textContent = original;
      });
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Dealer prices on Liquid-printed V2 prices ([data-sp-price]: cart add-ons, collection facts line).
     Impulse rebuilds the drawer in place, so watch it. Retail shoppers: nothing runs. */
  function initDealerPrices() {
    if (!(window.SP && SP.isDealer && SP.isDealer())) return;
    SP.repriceDealer(document);
    document.addEventListener('sp:dealer-ready', function () { SP.repriceDealer(document); });
    if (!('MutationObserver' in window)) return;
    var t;
    document.querySelectorAll('#CartDrawer, [data-sp-cd], [data-section-type="cart"]').forEach(function (host) {
      new MutationObserver(function () { clearTimeout(t); t = setTimeout(function () { SP.repriceDealer(document); }, 150); })
        .observe(host, { childList: true, subtree: true });
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Order attribution (same keys as BLCK ANVL and Big Gun 56039f2)          */
  /* ---------------------------------------------------------------------- */
  /* First and last touch utm_*, click ids, landing page and referrer live in localStorage (sp_attribution). Once
     the cart has an item they are written to it as cart attributes _ft_* / _lt_*, plus _fbp, _fbc and
     _ga_client_id when those cookies exist. The leading underscore keeps them off the checkout page; they show on
     the order under Additional details and in the orders export. Every add path in this theme is a fetch (Impulse
     product form, quick add, cart add-ons, kit builder, cart swap), so one fetch hook covers them; a no-JS form post
     lands on /cart with a cart cookie and the page-load check writes them there. Dealers keep the same cart, so
     /pages/ws-cart sees the attributes and nothing else. Buy it now skips the cart, so those orders carry none. */
  var ATTR_KEY = 'sp_attribution', ATTR_SENT = 'sp_attribution_sent';
  var TOUCH_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'ref'];
  function readCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }
  function captureAttribution() {
    try {
      var url = new URL(window.location.href);
      var touch = {}, hasTouch = false;
      TOUCH_PARAMS.forEach(function (k) { var v = url.searchParams.get(k); if (v) { touch[k] = v.slice(0, 200); hasTouch = true; } });
      var a = JSON.parse(localStorage.getItem(ATTR_KEY) || '{}');
      /* keep only the touch params on the landing page (the preview bar and Shopify add their own) */
      var keep = new URLSearchParams();
      TOUCH_PARAMS.forEach(function (k) { if (touch[k]) keep.set(k, touch[k]); });
      var stamp = Object.assign({}, touch, {
        landing_page: (url.pathname + (keep.toString() ? '?' + keep.toString() : '')).slice(0, 200),
        referrer: (document.referrer && document.referrer.indexOf(location.host) < 0 ? document.referrer : '').slice(0, 200),
        at: new Date().toISOString()
      });
      if (!a.first) a.first = stamp;
      if (hasTouch || !a.last) a.last = stamp;
      localStorage.setItem(ATTR_KEY, JSON.stringify(a));
    } catch (e) { /* storage blocked: nothing to carry */ }
  }
  function attributionAttrs() {
    var out = {};
    try {
      var a = JSON.parse(localStorage.getItem(ATTR_KEY) || '{}');
      [['ft', a.first], ['lt', a.last]].forEach(function (p) {
        if (!p[1]) return;
        Object.keys(p[1]).forEach(function (k) { if (k !== 'at' && p[1][k]) out['_' + p[0] + '_' + k] = p[1][k]; });
      });
    } catch (e) { /* ignore */ }
    var fbp = readCookie('_fbp'), fbc = readCookie('_fbc'), ga = readCookie('_ga');
    if (fbp) out._fbp = fbp;
    if (fbc) out._fbc = fbc;
    if (ga) out._ga_client_id = ga.replace(/^GA\d\.\d\./, '');
    return out;
  }
  var attrBusy = false, attrAgain = false;
  function writeAttribution(force) {
    if (attrBusy) { attrAgain = true; return; }
    var want = attributionAttrs();
    var sig = readCookie('cart') + '|' + JSON.stringify(want);
    try { if (!force && sessionStorage.getItem(ATTR_SENT) === sig) return; } catch (e) { /* no session storage */ }
    attrBusy = true;
    fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* an empty cart is remembered for the session too, so a shopper with an emptied cart does not cost a
           /cart.js read on every page; adds force a fresh check through the fetch hook, and /cart always checks */
        if (!cart || !cart.item_count) { try { sessionStorage.setItem(ATTR_SENT, sig); } catch (e) { /* ignore */ } return; }
        var have = cart.attributes || {}, diff = {}, n = 0;
        Object.keys(want).forEach(function (k) { if (have[k] !== want[k]) { diff[k] = want[k]; n++; } });
        /* cart attributes merge, so a newer touch without utm_medium would keep the older one: blank the leftovers */
        Object.keys(have).forEach(function (k) { if (/^_(ft|lt)_/.test(k) && !(k in want) && have[k] !== '') { diff[k] = ''; n++; } });
        var done = function () { try { sessionStorage.setItem(ATTR_SENT, sig); } catch (e) { /* ignore */ } };
        if (!n) return done();
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: diff })
        }).then(function (r) { if (r.ok) done(); });
      })
      .catch(function () { /* the order still goes through without them */ })
      .then(function () {
        attrBusy = false;
        if (attrAgain) { attrAgain = false; writeAttribution(true); }
      });
  }
  function initAttribution() {
    if (window.__spAttr) return;
    window.__spAttr = true;
    captureAttribution();
    var isAdd = function (u) { return /\/cart\/add(\.js)?(\?|$)/.test(String(u || '')); };
    if (window.fetch) {
      var f0 = window.fetch;
      window.fetch = function (input) {
        var p = f0.apply(this, arguments);
        var u = typeof input === 'string' ? input : (input && input.url);
        if (isAdd(u)) p.then(function (r) { if (r && r.ok) setTimeout(function () { writeAttribution(true); }, 300); }, function () {});
        return p;
      };
    }
    /* no cart cookie means no cart yet: skip the /cart.js read on first visits */
    if (readCookie('cart')) writeAttribution(/(^| )template-cart( |$)/.test(document.body.className));
  }

  /* ---------------------------------------------------------------------- */
  /* Nav V3 (sp2-nav, round 2 item 2, 2026-10-03). Markup: sections/header.liquid + snippets/sp-nav-*.liquid.
     Spec: Strategy/2026-10/nav-redesign/Nav-Redesign-Spec-2026-10-03.md. Ported from Big Gun biggun-v2.js
     initMegaNav / initDrawerNav / initNavGarage, plus the stuck header Impulse never turned on (its mega-height
     check killed sticky on every laptop). */
  var NAV = {};
  function navDesk() { return window.innerWidth >= 1025; }
  function navEsc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function navVisible(el) { return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length) && !el.closest('[hidden]') && !el.closest('[inert]'); }

  /* Stuck header: desktop after 250px (the compact 64px row with Menu), phones and tablets as soon as the header
     would leave the screen. The spacer keeps the unstuck height, so the page never jumps. */
  function initHeaderStick() {
    var hdr = document.querySelector('[data-sp-hdr]');
    var spacer = document.querySelector('[data-sp-hdr-spacer]');
    if (!hdr || !spacer || hdr.getAttribute('data-sp-init')) return;
    hdr.setAttribute('data-sp-init', '1');
    /* The shared Impulse search snippet carries the generic placeholder; the header field says what to search for */
    var sw = hdr.querySelector('[data-sp-search-placeholder]'), si = sw && sw.querySelector('input[type="search"]');
    if (si) si.setAttribute('placeholder', sw.getAttribute('data-sp-search-placeholder'));
    var stuck = false, ticking = false;
    /* Stuck, it publishes its real height as --sp-hdr-stuck-h: the PDP sticky bar parks under it from 990px (SP-69,
       sp-pdp.css) and the html scroll-padding clears both (stealth-v2.css). */
    function measure() { if (!stuck) spacer.style.minHeight = hdr.offsetHeight + 'px'; else document.documentElement.style.setProperty('--sp-hdr-stuck-h', hdr.offsetHeight + 'px'); }
    function update() {
      ticking = false;
      var top = spacer.getBoundingClientRect().top + window.scrollY;
      var want = navDesk() ? window.scrollY > 250 : window.scrollY > top;
      if (want === stuck) return;
      if (want) measure();
      stuck = want;
      hdr.classList.toggle('is-stuck', stuck);
      hdr.classList.toggle('is-slide', stuck && navDesk());
      if (stuck) measure();
      document.documentElement.classList.toggle('sp-hdr-stuck', stuck);
      if (stuck && NAV.closeMega) NAV.closeMega(false);
      if (!stuck && NAV.onUnstick) NAV.onUnstick();
    }
    window.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    window.addEventListener('resize', function () { measure(); update(); }, { passive: true });
    measure();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    window.addEventListener('load', measure);
    update();
  }

  /* Desktop mega (Sephora pattern, disclosure buttons). Mouse: opens after 120ms of hover, swaps at once along the
     bar, closes 200ms after the pointer leaves unless focus is inside the panel. Touch: first tap on a label opens,
     second follows the link. Keys: Enter, Space or ArrowDown on a chevron opens and focuses the first link;
     ArrowLeft / ArrowRight move along the bar; ArrowUp / ArrowDown move inside a panel; Escape closes and returns
     focus to the chevron; tabbing out closes. Closed panels are inert. */
  function initMegaNav() {
    var bar = document.querySelector('[data-sp-mega]');
    var hdr = document.querySelector('[data-sp-hdr]');
    if (!bar || !hdr || bar.getAttribute('data-sp-init')) return;
    bar.setAttribute('data-sp-init', '1');
    var scrim = hdr.querySelector('[data-sp-mega-scrim]');
    var tops = Array.prototype.slice.call(bar.querySelectorAll('.sp-bar__list > li'));
    var items = tops.filter(function (li) { return li.hasAttribute('data-sp-mega-item'); });
    var openItem = null, openT = null, closeT = null, lastPointer = 'mouse', openY = 0;
    function panelOf(li) { return li.querySelector('[data-sp-mega-panel]'); }
    function toggleOf(li) { return li.querySelector('[data-sp-mega-toggle]'); }
    function firstOf(li) { return li.querySelector('.sp-bar__label'); }
    function setOpen(li, on) {
      li.classList.toggle('is-open', on);
      var t = toggleOf(li); if (t) t.setAttribute('aria-expanded', on ? 'true' : 'false');
      var p = panelOf(li); if (p) { if (on) p.removeAttribute('inert'); else p.setAttribute('inert', ''); }
    }
    function open(li) {
      if (!navDesk() || hdr.classList.contains('is-stuck')) return;
      clearTimeout(openT); clearTimeout(closeT);
      if (openItem && openItem !== li) setOpen(openItem, false);
      if (!openItem) openY = window.scrollY;
      setOpen(li, true); openItem = li;
      hdr.classList.add('has-open-mega');
    }
    function closeAll(focusToggle) {
      clearTimeout(openT); clearTimeout(closeT);
      if (!openItem) return;
      var li = openItem; setOpen(li, false); openItem = null;
      hdr.classList.remove('has-open-mega');
      if (focusToggle) { var t = toggleOf(li); if (t) t.focus(); }
    }
    NAV.closeMega = closeAll;
    function focusFirst(li) { setTimeout(function () { var a = panelOf(li).querySelector('a[href], button'); if (a) a.focus(); }, 30); }
    items.forEach(function (li) {
      var t = toggleOf(li), label = firstOf(li);
      li.addEventListener('pointerenter', function (e) {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(closeT); clearTimeout(openT);
        /* R2A-04: stamp the hover open in both branches, so a click that follows moving from item to item keeps it open */
        /* R3-05: re-entering the item that is already open (out and back inside the 200ms close delay) keeps the old
           stamp, so a deliberate click to close it is not swallowed */
        if (openItem) { var fresh = openItem !== li; open(li); if (fresh) li.__hoverAt = Date.now(); } else openT = setTimeout(function () { open(li); li.__hoverAt = Date.now(); }, 120);
      });
      li.addEventListener('pointerleave', function (e) {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(openT);
        closeT = setTimeout(function () { if (openItem === li && !panelOf(li).contains(document.activeElement)) closeAll(false); }, 200);
      });
      if (label && label.tagName === 'A') {
        label.addEventListener('pointerdown', function (e) { lastPointer = e.pointerType; });
        label.addEventListener('click', function (e) {
          var touch = lastPointer === 'touch' || lastPointer === 'pen';
          lastPointer = 'mouse';
          if (touch && openItem !== li) { e.preventDefault(); open(li); }
        });
      }
      if (t) t.addEventListener('click', function (e) {
        e.preventDefault();
        /* D16 (F2-06): a mouse click that lands just after the hover opened this item keeps it open (it was closing it) */
        if (openItem === li && e.detail > 0 && li.__hoverAt && Date.now() - li.__hoverAt < 1500) { li.__hoverAt = 0; return; }
        if (openItem === li) { closeAll(false); return; }
        open(li);
        if (e.detail === 0) focusFirst(li);
      });
      li.addEventListener('focusout', function (e) {
        if (openItem === li && e.relatedTarget && !li.contains(e.relatedTarget)) closeAll(false);
      });
    });
    bar.addEventListener('keydown', function (e) {
      var li = e.target.closest('.sp-bar__list > li');
      if (!li) return;
      /* Impulse's predictive search blurs the focused element on any Escape (document listener), so the nav stops the
         event once it has handled it, or focus would fall to <body> instead of the chevron */
      if (e.key === 'Escape' && openItem) { e.preventDefault(); e.stopPropagation(); closeAll(true); return; }
      var inPanel = !!e.target.closest('[data-sp-mega-panel]');
      if (!inPanel) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          var i = tops.indexOf(li), n = tops[(i + (e.key === 'ArrowRight' ? 1 : -1) + tops.length) % tops.length];
          if (openItem && openItem !== n) closeAll(false);
          firstOf(n).focus();
          return;
        }
        if (e.key === 'ArrowDown' && li.hasAttribute('data-sp-mega-item')) { e.preventDefault(); open(li); focusFirst(li); }
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        var links = Array.prototype.slice.call(panelOf(li).querySelectorAll('a[href], button'));
        var k = links.indexOf(document.activeElement);
        if (k < 0) return;
        e.preventDefault();
        if (e.key === 'ArrowUp' && k === 0) { var tg = toggleOf(li); if (tg) tg.focus(); return; }
        links[Math.max(0, Math.min(links.length - 1, k + (e.key === 'ArrowDown' ? 1 : -1)))].focus();
      }
    });
    bar.addEventListener('click', function (e) {
      /* My bike opens over the page: close the panel first and park focus on its chevron, so the garage drawer hands
         focus back to a live control when it closes */
      if (e.target.closest('[data-sp-garage-open]')) closeAll(true);
    });
    document.addEventListener('click', function (e) { if (openItem && !openItem.contains(e.target)) closeAll(false); });
    if (scrim) scrim.addEventListener('click', function () { closeAll(false); });
    window.addEventListener('scroll', function () { if (openItem && Math.abs(window.scrollY - openY) > 40) closeAll(false); }, { passive: true });
    window.addEventListener('resize', function () { if (!navDesk()) closeAll(false); }, { passive: true });
  }

  /* Drawer (AutoZone pattern) for phones, tablets and the stuck desktop row. Focus moves into it and is trapped
     while open; Escape closes and returns focus to the Menu button; a chevron drills into a level and moves focus to
     Back; Back restores the list, its scroll position and focus on that chevron. Closed, the drawer is inert. */
  function initDrawerNav() {
    var drawer = document.querySelector('[data-sp-drawer]');
    if (!drawer || drawer.getAttribute('data-sp-init')) return;
    drawer.setAttribute('data-sp-init', '1');
    var scrim = document.querySelector('[data-sp-drawer-scrim]');
    var scroller = drawer.querySelector('[data-sp-drawer-scroll]');
    var root = drawer.querySelector('[data-sp-root]');
    var btns = Array.prototype.slice.call(document.querySelectorAll('[data-sp-drawer-open]'));
    var isOpen = false, lastBtn = null, opener = null, rootScroll = 0;
    function setBtns(on) {
      btns.forEach(function (b) {
        b.setAttribute('aria-expanded', on ? 'true' : 'false');
        var l = b.querySelector('[data-sp-menu-label]');
        if (l) l.textContent = on ? (b.getAttribute('data-label-close') || 'Close') : (b.getAttribute('data-label-open') || 'Menu');
      });
    }
    function focusables() {
      return Array.prototype.slice.call(drawer.querySelectorAll('a[href], button:not([disabled]), input, select')).filter(navVisible);
    }
    function showLevel(level, anim) {
      Array.prototype.forEach.call(drawer.querySelectorAll('[data-sp-level]'), function (l) { l.hidden = l !== level; l.classList.remove('is-entering'); });
      if (anim && level !== root) level.classList.add('is-entering');
    }
    function back(focus) {
      showLevel(root, false);
      scroller.scrollTop = rootScroll;
      if (opener) { opener.setAttribute('aria-expanded', 'false'); if (focus) opener.focus({ preventScroll: true }); }
      opener = null;
    }
    function openD(btn) {
      if (isOpen) return;
      if (NAV.closeMega) NAV.closeMega(false);
      lastBtn = btn || btns[0];
      isOpen = true;
      drawer.setAttribute('aria-modal', navDesk() ? 'false' : 'true');
      drawer.removeAttribute('inert');
      document.documentElement.classList.add('sp-nav-open');
      setBtns(true);
      setTimeout(function () {
        var c = drawer.querySelector('[data-sp-drawer-close]');
        var t = c && navVisible(c) ? c : focusables()[0];
        if (t) t.focus({ preventScroll: true });
      }, 50);
    }
    function closeD(focusBack) {
      if (!isOpen) return;
      isOpen = false;
      document.documentElement.classList.remove('sp-nav-open');
      if (opener) back(false);
      if (drawer.contains(document.activeElement) && focusBack === false) document.activeElement.blur();
      drawer.setAttribute('inert', '');
      setBtns(false);
      if (focusBack !== false && lastBtn && navVisible(lastBtn)) lastBtn.focus({ preventScroll: true });
    }
    NAV.closeDrawer = closeD;
    NAV.onUnstick = function () { if (isOpen && navDesk()) closeD(false); };
    btns.forEach(function (b) { b.addEventListener('click', function (e) { e.preventDefault(); if (isOpen) closeD(); else openD(b); }); });
    if (scrim) scrim.addEventListener('click', function () { closeD(); });
    drawer.addEventListener('click', function (e) {
      var drill = e.target.closest('[data-sp-drill]');
      if (drill) {
        var lvl = document.getElementById(drill.getAttribute('aria-controls'));
        if (!lvl) return;
        e.preventDefault();
        rootScroll = scroller.scrollTop;
        opener = drill;
        drill.setAttribute('aria-expanded', 'true');
        showLevel(lvl, true);
        scroller.scrollTop = 0;
        var bk = lvl.querySelector('[data-sp-back]'); if (bk) bk.focus({ preventScroll: true });
        return;
      }
      if (e.target.closest('[data-sp-back]')) { e.preventDefault(); back(true); return; }
      if (e.target.closest('[data-sp-drawer-close]')) { e.preventDefault(); closeD(); return; }
      /* My bike opens next (stealth-garage.js, on document): close the menu first so focus sits on Menu, which the
         garage drawer then returns to when it closes */
      if (e.target.closest('[data-sp-garage-open]')) closeD();
    });
    document.addEventListener('keydown', function (e) {
      if (!isOpen || document.body.classList.contains('sp-garage-open')) return;
      if (e.key === 'Escape') { e.preventDefault(); closeD(); return; }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (navDesk() && lastBtn && navVisible(lastBtn)) f.unshift(lastBtn);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1], a = document.activeElement;
      if (f.indexOf(a) < 0) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && a === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && a === last) { e.preventDefault(); first.focus(); }
    });
    window.addEventListener('resize', function () {
      var hdr = document.querySelector('[data-sp-hdr]');
      if (isOpen && navDesk() && hdr && !hdr.classList.contains('is-stuck')) closeD(false);
    }, { passive: true });
  }

  /* Every nav bike surface reads SP.Garage, the same state as the header button, the collection note and the PDP:
     the phone bike bar, the My bike blocks, and Popular bikes (saved bike first with a Yours tag). */
  function initNavBike() {
    function run() {
      var G = window.SP && window.SP.Garage;
      if (!G || document.documentElement.getAttribute('data-sp-navbike')) return;
      document.documentElement.setAttribute('data-sp-navbike', '1');
      function render() {
        var b = G.active();
        var label = b ? G.label(b) : '', url = b ? G.shopUrl(b) : '';
        var had = document.documentElement.classList.contains('sp-has-bike');
        document.documentElement.classList.toggle('sp-has-bike', !!b);
        /* the announcement slider measures itself while hidden; let it re-measure when the phone top row flips */
        if (had !== !!b) setTimeout(function () { window.dispatchEvent(new Event('resize')); }, 0);
        var bar = document.querySelector('[data-sp-bikebar]');
        if (bar && b) { bar.href = url; var nm = bar.querySelector('[data-sp-bikebar-name]'); if (nm) nm.textContent = label; }
        Array.prototype.forEach.call(document.querySelectorAll('.sp-navbike[data-sp-navbike]'), function (el) {
          var body = el.querySelector('[data-sp-navbike-body]');
          if (!body) return;
          if (el._empty == null) el._empty = body.innerHTML;
          if (!b) { body.innerHTML = el._empty; return; }
          body.innerHTML = '<p class="sp-navbike__name">' + navEsc(label) + '</p>' +
            (el.getAttribute('data-context') === 'panel' ? '<p class="sp-navbike__saved">' + navEsc(el.getAttribute('data-saved')) + '</p>' : '') +
            '<a class="sp-navbike__btn" href="' + navEsc(url) + '">' + navEsc(el.getAttribute('data-shop')) + '</a>' +
            '<button type="button" class="sp-navbike__change" data-sp-garage-open>' + navEsc(el.getAttribute('data-change')) + '</button>';
        });
        /* ul only: the product section also carries data-sp-popular (the most popular muffler), and matching it rewrote the
           whole buy box from its own HTML on every PDP, detaching Impulse's add to cart, Flickity and every V2
           listener (driver, round 2 item 6). The bike block selector skips the html flag set above for the same reason. */
        Array.prototype.forEach.call(document.querySelectorAll('ul[data-sp-popular]'), function (ul) {
          if (ul._orig == null) ul._orig = ul.innerHTML;
          ul.innerHTML = ul._orig;
          if (!b) return;
          var drawerCtx = ul.getAttribute('data-context') === 'drawer';
          var tag = '<span class="sp-nav-tag">' + navEsc(ul.getAttribute('data-yours') || 'Yours') + '</span>';
          var row = ul.querySelector('[data-gen="' + b.genId + '"]');
          if (row) {
            ul.insertBefore(row, ul.firstChild);
            var name = row.querySelector('.sp-drow__name, .sp-mega__name');
            if (name) { name.textContent = label; name.insertAdjacentHTML(drawerCtx ? 'beforeend' : 'afterend', tag); }
            var a = row.querySelector('a'); if (a) a.href = url;
          } else {
            var li = document.createElement('li');
            li.setAttribute('data-gen', b.genId);
            if (drawerCtx) {
              li.className = 'sp-drow';
              li.innerHTML = '<a class="sp-drow__link" href="' + navEsc(url) + '"><span class="sp-drow__two"><span class="sp-drow__name">' + navEsc(label) + tag + '</span></span></a>';
            } else {
              li.innerHTML = '<a href="' + navEsc(url) + '"><span class="sp-mega__name">' + navEsc(label) + '</span>' + tag + '</a>';
            }
            ul.insertBefore(li, ul.firstChild);
            while (ul.children.length > 6) ul.removeChild(ul.lastElementChild);
          }
        });
      }
      render();
      if (G.onChange) G.onChange(render);
    }
    if (window.SP && window.SP.Garage) run();
    else document.addEventListener('DOMContentLoaded', run);
    /* Dealers keep the announcement on phones (Wholesale Gorilla flags them after load) */
    function dealer() {
      if (!(window.SP && SP.isDealer && SP.isDealer()) || document.documentElement.classList.contains('sp-dealer')) return;
      document.documentElement.classList.add('sp-dealer');
      setTimeout(function () { window.dispatchEvent(new Event('resize')); }, 0);
    }
    dealer();
    document.addEventListener('DOMContentLoaded', dealer);
    document.addEventListener('wsg:data-ready', dealer);
  }


  /* ---------------------------------------------------------------------- */
  /* CTA analytics (design system, 2026-10-03): one delegated listener counts every button tap by style and surface,
     so the CTA colour strategy can be read before and after publish without per-button code. Disabled buttons keep
     pointer events, so taps on "Sold out" are counted too. Published like the reviews events (Shopify web pixels). */
  function initCtaEvents() {
    if (window.__spCtaInit) return;
    window.__spCtaInit = true;
    var styleOf = function (el) {
      if (el.disabled || el.getAttribute('aria-disabled') === 'true' || el.classList.contains('disabled')) return 'disabled';
      if (el.matches('.btn--addon, .sp-kit__btn, .sp-addon__btn, .sp-kit .sp-picker__save')) return 'addon';
      if (el.matches('.btn--tertiary, .text-link, .shopify-payment-button__more-options')) return 'tertiary';
      if (el.matches('.btn--secondary:not(.add-to-cart), .sp-mp-more, .sp-garage__shop, .sp-profile-card__cta, .quick-product__btn')) return 'secondary';
      return 'primary';
    };
    var surfaceOf = function (el) {
      if (el.closest('.sp-sticky, .sp-sticky3')) return 'pdp_sticky';
      if (el.closest('.sp-kit--cart')) return 'kit_cart';
      if (el.closest('.sp-kit')) return 'kit_pdp';
      if (el.closest('.drawer__contents, #CartDrawer, [data-sp-cd]')) return 'cart_drawer';
      if (el.closest('.sp-garage')) return 'garage';
      if (el.closest('.cart__page, .cart__page-col')) return 'cart_page';
      if (el.closest('.product-single__meta, .payment-buttons')) return 'pdp_buybox';
      if (el.closest('.grid-product')) return 'card_' + ((document.body.className.match(/template-([a-z0-9-]+)/) || [])[1] || 'page');
      if (el.closest('.hero, .slideshow-wrapper')) return 'hero';
      if (el.closest('.sp-finder__mount, .sp-finder')) return 'finder';
      if (el.closest('.sp-profiles, .sp-profile-card')) return 'profiles';
      if (el.closest('.background-media-text, .newsletter-section, .feature-row')) return 'band';
      return 'page';
    };
    document.addEventListener('click', function (e) {
      var el = e.target.closest('.btn, .sp-profile-card__cta, .text-link, .quick-product__btn');
      if (!el) return;
      var data = { style: styleOf(el), surface: surfaceOf(el), label: (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 60) };
      try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('sp_cta', data); } catch (err) {}
    }, true);
    /* Browsers do not fire click on a [disabled] button (sold-out Add to cart), but pointer events still reach it */
    document.addEventListener('pointerup', function (e) {
      var el = e.target.closest && e.target.closest('.btn[disabled], button.sp-card__atc[disabled]');
      if (!el) return;
      var data = { style: 'disabled', surface: surfaceOf(el), label: (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 60) };
      try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('sp_cta', data); } catch (err) {}
    }, true);
  }

  /* ---------------------------------------------------------------------- */
  /* Inline header search (nav-v3 D5) reuses Impulse's predictive-search element, built for a modal: any keydown in the
     field (Tab too) sets is-active, which shows a fixed full-page .predictive__screen, and only Escape, a click on that
     screen or the close button (hidden at desktop) clears it. Tabbing past the field left the page covered and
     unclickable (item 7.1). The search now closes when focus or a click leaves it; CSS shows the screen only while
     there are results. */
  function initInlineSearch() {
    document.querySelectorAll('.sp-hdr__search predictive-search').forEach(function (ps) {
      if (ps.dataset.spInline) return;
      ps.dataset.spInline = '1';
      /* Same reset as Impulse's close(), without its predictive-search:close-all event: that handler blurs
         document.activeElement (it was written for the old search drawer), so the control the shopper just tabbed to
         lost focus and the next Tab started from the body. */
      var shut = function () {
        if (!ps.classList.contains('is-active')) return;
        var r = ps.querySelector('#predictive-search');
        if (r) { r.style.display = 'none'; r.innerHTML = ''; }
        ps.classList.remove('is-active');
        document.body.classList.remove('predictive-overflow-hidden');
      };
      ps.addEventListener('focusout', function () {
        setTimeout(function () { if (!ps.contains(document.activeElement)) shut(); }, 0);
      });
      document.addEventListener('pointerdown', function (e) { if (!ps.contains(e.target)) shut(); });
    });
  }

  function boot() {
    initHeaderStick();
    initMegaNav();
    initDrawerNav();
    initNavBike();
    initAttribution();
    initInlineSearch();
    document.querySelectorAll('[data-section-type="product"]').forEach(initProduct);
    initAddons();
    initDealerPrices();
    initCtaEvents();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  /* Quick view modals inject a product section later */
  document.addEventListener('quickview:loaded', function (e) {
    var root = (e && e.detail && e.detail.container) || document;
    (root.querySelectorAll ? root : document).querySelectorAll('[data-section-type="product"]').forEach(initProduct);
  });
  document.addEventListener('shopify:section:load', boot);
  /* D14: closing the phone search (Close, Escape or the screen) blurs to <body> in Impulse; hand focus back to the
     search button so a keyboard or screen reader user keeps their place. */
  var escAt = 0;
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') escAt = Date.now(); }, true);
  document.addEventListener('predictive-search:close-all', function () {
    setTimeout(function () {
      var a = document.activeElement;
      if (a && a !== document.body && a !== document.documentElement) return;
      if (window.innerWidth >= 1000) {
        /* D16 (F2-20): desktop keeps the search inline, so Escape returns focus to its input (not after a click elsewhere) */
        if (Date.now() - escAt > 600) return;
        var inp = document.querySelector('.sp-hdr__search input[type="search"]');
        if (inp) inp.focus();
        return;
      }
      var b = document.querySelector('.sp-hdr__search-btn');
      if (b && b.offsetParent) b.focus();
    }, 60);
  });
  /* D16 (F2-19): on phones an open search with an EMPTY field has no dimmed screen to tap (V2 hides it while empty), so a
     tap outside the search closes it the same way the screen does with results. */
  document.addEventListener('click', function (e) {
    if (window.innerWidth >= 1000) return;
    var box = document.querySelector('.sp-hdr__search.is-active');
    if (!box || box.contains(e.target) || e.target.closest('.sp-hdr__search-btn, .js-search-header')) return;
    var inp = box.querySelector('input[type="search"]');
    if (inp && inp.value.trim() === '') document.dispatchEvent(new CustomEvent('predictive-search:close'));
  });
  /* D15 (gate H3): the server prints only the relative build time ("Ships in 2 to 4 weeks"), because cached Liquid
     'now' goes stale. Here the visible line gets today's dates from Date.now(), with the weeks the server already
     resolved (finish adders included in the cart). JSON-LD and view-source never carry a date. */
  function leadDates(root) {
    var wk = 604800000, now = Date.now();
    var fmt = function (ms) { return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
    (root || document).querySelectorAll('[data-lead-time][data-dates]').forEach(function (el) {
      /* R2A-01: the PDP build line belongs to sp-pdp.js (syncBuild), which dates it for the SELECTED finish; this base
         window must never overwrite it (a cart change used to revert Polished or Cerakote to the Raw window) */
      if (el.closest('[data-sp-buildline][data-dyn]')) return;
      var min = parseInt(el.dataset.min, 10), max = parseInt(el.dataset.max, 10);
      var line = el.querySelector('[data-lead-line]');
      if (!line || isNaN(min) || isNaN(max)) return;
      var from = fmt(now + min * wk), to = fmt(now + max * wk);
      if (el.dataset.dates === 'short') {
        var tpl = el.dataset.tplShort || '';
        if (tpl.indexOf('[from]') > -1) line.textContent = tpl.replace('[from]', from).replace('[to]', to);
      } else {
        var d = line.querySelector('[data-lead-dates]');
        if (d && !d.textContent) d.textContent = ' (' + from + ' to ' + to + ')';
      }
    });
  }
  window.SP = window.SP || {};
  SP.leadDates = leadDates;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { leadDates(); });
  else leadDates();
  /* the cart drawer and /cart re-render from server HTML after every change */
  document.addEventListener('sp:cart:change', function () { leadDates(); });
  document.addEventListener('cart:updated', function () { setTimeout(leadDates, 0); });
  /* D15 (WCAG 4.1.3): predictive search says how many results it found, once the list settles. No combobox role here,
     so no aria-expanded or aria-controls; the results follow the input in the DOM and in Tab order. */
  document.querySelectorAll('[data-sp-ps-status]').forEach(function (status) {
    var form = status.closest('form') || document;
    var box = form.querySelector('#predictive-search');
    var input = form.querySelector('input[type="search"]');
    if (!box || !('MutationObserver' in window)) return;
    var t;
    var say = function () {
      var q = input ? input.value.trim() : '';
      if (!q) { status.textContent = ''; return; }
      var res = box.querySelector('#predictive-search-results');
      if (!res) { status.textContent = ''; return; }
      /* count what is shown: Impulse prints some groups twice and hides one copy (F2-11) */
      var n = Array.prototype.filter.call(res.querySelectorAll('li a[href]'), function (x) { return x.getClientRects().length > 0; }).length;
      status.textContent = n ? (n === 1 ? '1 result' : n + ' results') : 'No results';
    };
    new MutationObserver(function () { clearTimeout(t); t = setTimeout(say, 400); }).observe(box, { childList: true, subtree: true });
  });
})();
